import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentProviderBadge } from '@/modules/payments/components/PaymentProviderBadge';
import { PaymentStatusBadge } from '@/modules/payments/components/PaymentStatusBadge';
import { PortalStaggerItem } from '@/modules/portal/components/ui/portal-motion';
import { downloadPortalPaymentReceipt } from '@/modules/portal/services/portalPayments.api';
import type { PortalInvoiceRecord } from '@/modules/portal/utils/portalInvoicesHelpers';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { useTenant } from '@/providers/TenantProvider';
import { formatMoney } from '@/shared/utils/money';
import { cn } from '@/shared/lib/cn';

interface PortalInvoiceCardProps {
  record: PortalInvoiceRecord;
  locale: string;
}

export function PortalInvoiceCard({ record, locale }: PortalInvoiceCardProps) {
  const { t } = useTranslation();
  const { activeTenantId } = useTenant();
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState(false);

  const { payment, invoiceReference, providerReference, documentDate, productName, planName } =
    record;
  const subscriptionLabel =
    productName || planName ? [productName, planName].filter(Boolean).join(' · ') : null;
  const documentLabel = invoiceReference
    ? t('portal.invoices.document.labeled')
    : t('portal.invoices.document.receipt');

  const handleReceipt = async () => {
    if (!activeTenantId || !record.canDownloadReceipt) return;
    setReceiptError(false);
    setReceiptLoading(true);
    try {
      await downloadPortalPaymentReceipt(payment.id, activeTenantId);
    } catch {
      setReceiptError(true);
    } finally {
      setReceiptLoading(false);
    }
  };

  return (
    <PortalStaggerItem>
      <article
        className={cn(
          'portal-glass-panel flex flex-col gap-4 rounded-2xl p-5 sm:p-6',
          'border border-border/60',
        )}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {documentLabel}
            </p>
            {invoiceReference ? (
              <p className="break-all font-mono text-sm font-semibold text-foreground">
                {invoiceReference}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('portal.invoices.fields.noReference')}
              </p>
            )}
            <p className="text-sm text-muted-foreground tabular-nums">{documentDate}</p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <PaymentStatusBadge status={payment.status} />
            {payment.provider ? (
              <PaymentProviderBadge provider={payment.provider} label={payment.provider} />
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-2xl font-bold tracking-tight text-foreground tabular-nums">
            {formatMoney(payment.amount, payment.currency, locale === 'ar-SA' ? 'ar' : 'en')}
          </span>
          <span className="text-sm font-medium uppercase text-muted-foreground">
            {payment.currency}
          </span>
        </div>

        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          {subscriptionLabel ? (
            <div className="min-w-0">
              <dt className="text-muted-foreground">{t('portal.invoices.fields.plan')}</dt>
              <dd className="mt-0.5 font-medium text-foreground">{subscriptionLabel}</dd>
            </div>
          ) : null}
          {providerReference ? (
            <div className="min-w-0 sm:col-span-2">
              <dt className="text-muted-foreground">
                {t('portal.invoices.fields.providerReference')}
              </dt>
              <dd className="mt-0.5 break-all font-mono text-xs text-foreground">
                {providerReference}
              </dd>
            </div>
          ) : null}
        </dl>

        {record.canDownloadReceipt ? (
          <div className="flex flex-col gap-2 border-t border-border/50 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">{t('portal.invoices.receipt.hint')}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={receiptLoading || !activeTenantId}
              onClick={() => void handleReceipt()}
              className="gap-2 self-start sm:self-auto"
            >
              {receiptLoading ? (
                <Icons.RefreshCw
                  className="h-4 w-4 animate-spin motion-reduce:animate-pulse"
                  aria-hidden
                />
              ) : (
                <Icons.Download className="h-4 w-4" aria-hidden />
              )}
              {t('portal.invoices.receipt.download')}
            </Button>
          </div>
        ) : null}

        {receiptError ? (
          <p className="text-xs text-destructive" role="alert">
            {t('portal.invoices.receipt.error')}
          </p>
        ) : null}
      </article>
    </PortalStaggerItem>
  );
}
